/**
 * A human-typed hand-off, answerable in place (spec #46 §2's two
 * human-consumed types, surfaced by #44's accepted delta 4).
 *
 * Only `tool_request` and `review_request` ever reach this card. A blocked
 * item is shown blocked WITH its reason — it stays `open` server-side and
 * is never checked out, and hiding it would make an agent look idle when
 * it is actually waiting on a gate.
 */

import { Badge, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';

import { humanHandoffLabel } from '../../contexts/workspaceComposition';
import type { HandoffOutcome } from '../../types/handoffs';
import type { WorkspaceQuestion } from '../../types/workspace';
import { QuestionOwner } from './QuestionCard';
import styles from './workspace.module.css';

export interface HandoffCardProps {
  question: WorkspaceQuestion;
  agentNames: Record<string, string>;
  onResolve: (
    question: WorkspaceQuestion,
    outcome: HandoffOutcome,
    reason?: string
  ) => void;
  busy?: boolean;
}

export function HandoffCard({
  question,
  agentNames,
  onResolve,
  busy = false,
}: HandoffCardProps) {
  const handoff = question.handoff;
  if (!handoff) return null;

  return (
    <div className={styles.questionCard} data-testid={`handoff-card-${handoff.id}`}>
      <Card>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="attention">{humanHandoffLabel(handoff.type)}</Badge>
            <Text as="span" tone="subdued">
              from {handoff.from_kind} · {handoff.from_key}
            </Text>
          </InlineStack>
          <QuestionOwner question={question} agentNames={agentNames} />
          <Text as="p">{question.title}</Text>
          {handoff.blocked ? (
            <Text as="p" tone="subdued">
              Blocked: {handoff.blocked_reason ?? 'a gate is holding this'}
            </Text>
          ) : null}
          <InlineStack gap="200">
            <Button
              variant="primary"
              loading={busy}
              onClick={() => onResolve(question, 'done')}
            >
              Mark handled
            </Button>
            <Button loading={busy} onClick={() => onResolve(question, 'declined')}>
              Decline
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    </div>
  );
}
